import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  alpha,
  useTheme,
  useMediaQuery
} from '@mui/material';
import Settings from '@mui/icons-material/Settings';
import { formatProduct } from '../../../utils/utilfunction';

const SettingsDialog = ({
  open,
  onClose,
  orderType,
  setOrderType,
  productType,
  setProductType,
  defaultQtyByExchange,
  setDefaultQtyByExchange,
  onSave,
  isUpdating,
  updateError
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  const exchanges = ['NSE', 'BSE', 'NFO', 'BFO', 'MCX', 'DCSX', 'CDS'];

  const handleQtyChange = (exchange, value) => {
    setDefaultQtyByExchange(prev => {
      const updated = { ...prev };
      if (value === '') {
        updated[exchange] = '';
      } else {
        const num = parseInt(value, 10);
        updated[exchange] = isNaN(num) ? '' : num;
      }
      return updated;
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 4 },
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          backgroundImage: `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.background.paper, 0.95)})`,
          overflow: 'hidden'
        }
      }}
    >
      <Box sx={{ 
        p: { xs: 2.5, sm: 3 }, 
        background: theme.palette.mode === 'dark'
          ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
          : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 20px 20px, rgba(255,255,255,0.15) 2px, transparent 0)',
          backgroundSize: '40px 40px',
          opacity: 0.5
        }
      }}>
        <Avatar sx={{ 
          bgcolor: 'rgba(255,255,255,0.25)',
          width: { xs: 40, sm: 48 },
          height: { xs: 40, sm: 48 },
          boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
        }}>
          <Settings sx={{ color: 'white', fontSize: { xs: '1.4rem', sm: '1.6rem' } }} />
        </Avatar>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, textShadow: '0 2px 4px rgba(0,0,0,0.1)', fontSize: { xs: '1.15rem', sm: '1.25rem' } }}>
          Edit Trading Settings
        </Typography>
      </Box>

      <DialogContent sx={{ py: { xs: 2.5, sm: 3.5 }, px: { xs: 2.5, sm: 3.5 } }}>
        {updateError && (
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            color: theme.palette.error.main
          }}>
            <Typography variant="body2">{updateError}</Typography>
          </Box>
        )}
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 700, 
                mb: 0.5, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.2, 
                fontSize: { xs: '0.95rem', sm: '1rem' } 
              }}>
                <Box component="span" sx={{ 
                  width: 10, 
                  height: 10, 
                  borderRadius: '50%', 
                  bgcolor: theme.palette.primary.main,
                  display: 'inline-block',
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`
                }} />
                Order Type
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, pl: 3, fontSize: '0.8rem' }}>
                Default order type for placing new trades
              </Typography>
            </Box>
            <FormControl fullWidth variant="outlined" size="small">
              <Select
                value={orderType}
                onChange={(e) => setOrderType(e.target.value)}
                sx={{ 
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    borderWidth: 2
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                  borderRadius: 2,
                }}
              >
                <MenuItem value="LIMIT">LIMIT</MenuItem>
                <MenuItem value="MARKET">MARKET</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 700, 
                mb: 0.5, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.2, 
                fontSize: { xs: '0.95rem', sm: '1rem' } 
              }}>
                <Box component="span" sx={{ 
                  width: 10, 
                  height: 10, 
                  borderRadius: '50%', 
                  bgcolor: theme.palette.secondary.main,
                  display: 'inline-block',
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.2)}`
                }} />
                Product Type
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, pl: 3, fontSize: '0.8rem' }}>
                Default product type for placing new trades
              </Typography>
            </Box>
            <FormControl fullWidth variant="outlined" size="small">
              <Select
                value={productType}
                onChange={(e) => setProductType(e.target.value)}
                sx={{ 
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.secondary.main, 0.3),
                    borderWidth: 2
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.secondary.main, 0.5),
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary.main,
                  },
                  borderRadius: 2,
                }}
              >
                <MenuItem value="CNC/NRML">{formatProduct("CNC/NRML")}</MenuItem>
                <MenuItem value="MIS">{formatProduct("MIS")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ 
            fontWeight: 700, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.2, 
            fontSize: { xs: '0.95rem', sm: '1rem' } 
          }}>
            <Box component="span" sx={{ 
              width: 10, 
              height: 10, 
              borderRadius: '50%', 
              bgcolor: theme.palette.info.main,
              display: 'inline-block',
              boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.2)}`
            }} />
            Default Quantities by Exchange
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, pl: 3, mb: 2, fontSize: '0.8rem' }}>
            Set a default quantity to pre-fill in the order window.
          </Typography>

          <Grid container spacing={1.5} sx={{ pl: 3 }}>
            {exchanges.map((exchange) => (
              <Grid item xs={6} sm={4} md={3} key={exchange}>
                <TextField
                  label={exchange}
                  variant="outlined"
                  size="small"
                  fullWidth
                  type="number"
                  placeholder="1"
                  value={defaultQtyByExchange?.[exchange] || ''}
                  onChange={(e) => handleQtyChange(exchange, e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        px: { xs: 2.5, sm: 3.5 }, 
        py: { xs: 2, sm: 2.5 }, 
        gap: 1.5,
        backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.2) : alpha(theme.palette.grey[50], 0.8),
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        backdropFilter: 'blur(8px)',
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ 
            borderRadius: 8, 
            textTransform: 'none', 
            fontWeight: 600,
            px: 3,
            py: 0.8,
            fontSize: '0.9rem',
            borderColor: alpha(theme.palette.text.primary, 0.2),
            color: theme.palette.text.primary,
            '&:hover': {
              borderColor: alpha(theme.palette.text.primary, 0.5),
              backgroundColor: alpha(theme.palette.text.primary, 0.03),
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={onSave}
          variant="contained"
          disabled={isUpdating}
          sx={{ 
            borderRadius: 8, 
            textTransform: 'none', 
            fontWeight: 600,
            px: 4,
            py: 0.8,
            fontSize: '0.9rem',
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
            background: theme.palette.mode === 'dark'
              ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
              : `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            '&:hover': {
              boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
              background: theme.palette.mode === 'dark'
                ? `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`
                : `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
            },
            '&.Mui-disabled': {
              background: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled
            }
          }}
          startIcon={isUpdating ? <Box sx={{ 
            width: 16, 
            height: 16, 
            borderRadius: '50%', 
            border: '2px solid currentColor',
            borderTopColor: 'transparent',
            animation: 'spin 1s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            }
          }} /> : null}
        >
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog; 